import React, { useEffect, useMemo, useState } from "react";
import { workflowService } from "../../services/api/workflow.service";
import { UserStatusId } from "../../types/user.types";
import type {
  AppliedWorkflowAction,
  QueryWorkflowActionDto,
} from "../../types/workflow.types";
import { REQUIRED_FIELD_MESSAGE } from "../../utils/constants";
import { showConfirm } from "../../utils/toast";
import "../../styles/pages/QueryApplicationForm.css";

interface QueryApplicationFormProps {
  isOpen: boolean;
  onClose: () => void;
  entityName: string;
  recordId?: number;
  workflowId?: number;
  statusId?: number;
  onSubmit: (payload: QueryWorkflowActionDto) => Promise<void>;
}

const QueryApplicationForm: React.FC<QueryApplicationFormProps> = ({
  isOpen,
  onClose,
  entityName,
  recordId,
  workflowId,
  statusId,
  onSubmit,
}) => {
  const [loadingActions, setLoadingActions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [query, setQuery] = useState("");
  const [queryForDataEntry, setQueryForDataEntry] = useState(true);
  const [targetActionId, setTargetActionId] = useState<number | undefined>(
    undefined,
  );
  const [appliedActions, setAppliedActions] = useState<AppliedWorkflowAction[]>(
    [],
  );

  const isSubmittedStatus =
    statusId === UserStatusId.Submitted || statusId === UserStatusId.Queried;
  const hasAppliedActions = appliedActions.length > 0;
  const showDataEntrySwitch = isSubmittedStatus && hasAppliedActions;
  const showTargetSelect = showDataEntrySwitch && !queryForDataEntry;

  useEffect(() => {
    if (!isOpen || !recordId) return;

    const fetchAppliedActions = async () => {
      try {
        setLoadingActions(true);
        setError("");
        const data = await workflowService.getAppliedActions(
          entityName,
          recordId,
        );
        setAppliedActions(Array.isArray(data) ? data : []);
      } catch (err: any) {
        setAppliedActions([]);
        setError(err?.message || "Failed to load workflow actions");
      } finally {
        setLoadingActions(false);
      }
    };

    fetchAppliedActions();
  }, [entityName, isOpen, recordId]);

  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setTargetActionId(undefined);
    setQueryForDataEntry(true);
    setError("");
    setValidationErrors({});
  }, [isOpen, recordId]);

  const actionOptions = useMemo(
    () =>
      appliedActions.map((action) => ({
        id: action.id,
        workflowStageId: action.workflowStageId,
        label: `${action.id} - ${action.roleName || "Unknown Role"}`,
      })),
    [appliedActions],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: Record<string, string> = {};

    if (!query.trim()) {
      errors.query = REQUIRED_FIELD_MESSAGE;
    }

    if (showTargetSelect && !targetActionId) {
      errors.targetActionId = REQUIRED_FIELD_MESSAGE;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (!workflowId || !recordId) {
      setError("Missing workflow details for query submission.");
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Query",
      "Are you sure you want to submit this query? This action will send your remarks to the appropriate stage.",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setSubmitting(true);
      setError("");
      setValidationErrors({});

      const selectedAction = showTargetSelect
        ? actionOptions.find((option) => option.id === targetActionId)
        : undefined;

      const resolvedWorkflowStageId = showTargetSelect
        ? (selectedAction?.workflowStageId ?? null)
        : null;

      if (
        showTargetSelect &&
        (!resolvedWorkflowStageId || resolvedWorkflowStageId <= 0)
      ) {
        setError("Unable to determine a valid workflow stage for this query.");
        setSubmitting(false);
        return;
      }

      const payload: QueryWorkflowActionDto = {
        workflowId,
        entityName,
        recordId,
        query: query.trim(),
        workflowStageId: resolvedWorkflowStageId,
      };

      console.log("[QueryApplicationForm] Submit payload:", payload);

      await onSubmit(payload);

      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to submit query");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay modal-overlay--side-panel" onClick={onClose}>
      <div
        className="modal-content modal-content--side-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">Query Application</h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            disabled={submitting}
          >
            ✕
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError("")}
                className="alert-close"
              >
                ✕
              </button>
            </div>
          )}

          {loadingActions && (
            <div className="alert">Loading workflow options...</div>
          )}

          {showDataEntrySwitch && (
            <div className="form-group has-value">
              <div className="query-switch-row">
                <label
                  className="query-switch"
                  aria-label="Query for Data Entry"
                >
                  <input
                    type="checkbox"
                    checked={queryForDataEntry}
                    onChange={(e) => setQueryForDataEntry(e.target.checked)}
                  />
                  <span className="query-switch-slider"></span>
                </label>
                <span className="query-switch-value">Query for Data Entry</span>
              </div>
            </div>
          )}

          {showTargetSelect && (
            <div
              className={`form-group has-value ${validationErrors.targetActionId ? "form-group--error" : ""}`}
            >
              <select
                className={`form-input ${validationErrors.targetActionId ? "form-input--error" : ""}`}
                value={targetActionId ?? ""}
                onChange={(e) => {
                  setTargetActionId(
                    e.target.value ? parseInt(e.target.value, 10) : undefined,
                  );
                  if (validationErrors.targetActionId) {
                    setValidationErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.targetActionId;
                      return newErrors;
                    });
                  }
                }}
                required
              >
                <option value="">Select recipient</option>
                {actionOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label || `Action ${option.id}`}
                  </option>
                ))}
              </select>
              <label className="form-label">Query Intended For</label>
              {validationErrors.targetActionId && (
                <div className="field-error">
                  {validationErrors.targetActionId}
                </div>
              )}
            </div>
          )}

          <div
            className={`form-group ${query ? "has-value" : ""} ${validationErrors.query ? "form-group--error" : ""}`}
          >
            <textarea
              className={`form-input ${validationErrors.query ? "form-input--error" : ""}`}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (validationErrors.query) {
                  setValidationErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors.query;
                    return newErrors;
                  });
                }
              }}
              placeholder=" "
              style={{ minHeight: "120px", resize: "vertical" }}
              required
            />
            <label className="form-label">
              Query Remark <span className="required">*</span>
            </label>
            {validationErrors.query && (
              <div className="field-error">{validationErrors.query}</div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn--outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit Query"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QueryApplicationForm;
